// A segmented control whose pill slides with the same spring used
// by the bottom sheet. In the marketplace layout it doubles as
// quick-jump navigation: tapping a segment smooth-scrolls to that
// section rather than hiding/showing content, since every section
// is visible on one scrollable page.

import { useEffect, useRef } from 'react';
import { cx } from '@/utils/cx';
import { useSpring } from '@/hooks/useSpring';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import styles from './QuickNav.module.scss';

export interface QuickNavOption {
  value: string;
  label: string;
}

export interface QuickNavProps {
  options: QuickNavOption[];
  active: string;
  onChange: (value: string) => void;
}

export function QuickNav({ options, active, onChange }: QuickNavProps) {
  const pillRef = useRef<HTMLSpanElement | null>(null);
  const reducedMotion = useReducedMotion();

  const spring = useSpring(0, {
    damping: 1.0,
    response: 0.32,
    onUpdate: (value) => {
      if (pillRef.current) {
        pillRef.current.style.transform = `translateX(${value}%)`;
      }
    },
  });

  useEffect(() => {
    const index = Math.max(
      0,
      options.findIndex((option) => option.value === active),
    );
    const target = index * 100;
    if (reducedMotion) {
      spring.jumpTo(target);
    } else {
      spring.setTarget(target);
    }
  }, [active, options, reducedMotion, spring]);

  return (
    <nav
      className={styles.segmented}
      style={{ gridTemplateColumns: `repeat(${options.length}, 1fr)` }}
      aria-label="Jump to section"
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-current={option.value === active ? 'true' : undefined}
          className={cx(styles.button, option.value === active && styles.active)}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
      <span
        ref={pillRef}
        className={styles.pill}
        style={{ width: `calc(${100 / options.length}% - 3px)` }}
        aria-hidden="true"
      />
    </nav>
  );
}
